import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { REGION_META } from '../data/team';
import { CATEGORIES, CAT_COLOR } from '../data/qcCategories';
import QCReportModal from '../components/QCReportModal';

const regionHex = (r) => (REGION_META[r] && REGION_META[r].hex) || '#8A949A';
const titleCase = (s) => String(s || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());


const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

// Modal to enter resolution notes when closing a ticket
function ResolveModal({ ticket, onClose, onResolve }) {
  const [notes, setNotes] = useState(ticket.resolution_notes || '');
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    await onResolve(ticket.id, notes);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <form onSubmit={submit} className="w-full max-w-lg rounded-xl border border-navy-500 p-6" style={{ backgroundColor: '#142236' }}>
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-white font-bold text-lg">Resolve QC Ticket</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white transition text-xl leading-none">&times;</button>
        </div>
        <p className="text-gray-400 text-xs mb-4 font-mono">{ticket.project_id}{ticket.tech ? ` · ${ticket.tech}` : ''}</p>

        <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Resolution Notes</label>
        <textarea
          autoFocus
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="How was it resolved? (who fixed it, what changed…)"
          className="w-full rounded px-3 py-2 text-sm text-white placeholder-gray-500 border focus:outline-none resize-y"
          style={{ backgroundColor: '#0f1a29', borderColor: '#1e3a5f' }}
        />

        <div className="flex gap-3 mt-5">
          <button type="submit" disabled={saving} className="flex-1 py-2 rounded font-bold uppercase tracking-wide text-sm disabled:opacity-50 transition" style={{ backgroundColor: '#22c55e', color: '#06210f' }}>
            {saving ? 'Resolving…' : '✓ Mark Resolved'}
          </button>
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded border border-navy-500 text-gray-400 text-sm hover:text-white hover:border-gray-400 transition">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// Full QC history for one person — every ticket ever filed against them,
// newest first, opened by clicking their name anywhere on the page.
function TechHistoryModal({ tech, tickets, onClose }) {
  const mine = tickets
    .filter((t) => (t.tech || '').trim().toLowerCase() === tech.trim().toLowerCase())
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const open = mine.filter((t) => !t.resolved).length;

  // Category tallies for the header
  const cats = {};
  mine.forEach((t) => { if (t.category) cats[t.category] = (cats[t.category] || 0) + 1; });
  const catList = Object.entries(cats).sort((a, b) => b[1] - a[1]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[80vh] flex flex-col rounded-xl border border-navy-500"
        style={{ backgroundColor: '#142236' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 pb-4 border-b" style={{ borderColor: '#1e3a5f' }}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-white font-bold text-lg">{tech}</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {mine.length} QC flag{mine.length === 1 ? '' : 's'} all time
                {open > 0 && <span className="text-yellow-400"> · {open} open</span>}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-white transition text-xl leading-none">&times;</button>
          </div>
          {catList.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {catList.map(([c, n]) => (
                <span key={c} className="text-xs font-bold px-2 py-0.5 rounded" style={{ color: CAT_COLOR[c] || '#9ca3af', backgroundColor: '#0f1a29' }}>
                  {c} × {n}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="overflow-y-auto p-5 space-y-3">
          {mine.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No QC tickets for {tech}.</p>
          ) : (
            mine.map((t) => (
              <div key={t.id} className="border rounded-lg p-3" style={{ backgroundColor: t.resolved ? '#0b1622' : '#0d1b2e', borderColor: t.resolved ? '#13212f' : '#1e3a5f' }}>
                <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-xs text-gray-400">{t.project_id || '—'}</span>
                    <CategoryBadge category={t.category} />
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${t.resolved ? 'text-green-400' : 'text-yellow-400'}`} style={{ backgroundColor: '#0f1a29' }}>
                      {t.resolved ? 'Resolved' : 'Open'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 shrink-0">{t.qcd_by} · {fmtDate(t.created_at)}</span>
                </div>
                <p className="text-sm text-gray-200 whitespace-pre-wrap">{t.note || <span className="text-gray-600 italic">No note</span>}</p>
                {t.resolved && t.resolution_notes && (
                  <p className="text-xs text-gray-400 mt-1.5 whitespace-pre-wrap"><span className="text-gray-500 uppercase">Resolution:</span> {t.resolution_notes}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryBadge({ category }) {
  if (!category) return <span className="text-xs text-gray-600">—</span>;
  return (
    <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ color: CAT_COLOR[category] || '#9ca3af', backgroundColor: '#0f1a29' }}>
      {category}
    </span>
  );
}

// One region's card in the "Mistakes by Region" section: who's getting flagged
// there and for what, colored to match the directory's region palette.
function RegionCard({ region, onTechClick }) {
  const hex = regionHex(region.region);
  const maxCat = region.byCategory[0]?.count || 1;
  return (
    <div className="border rounded-lg p-4" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f', borderTopColor: hex, borderTopWidth: 3 }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: hex }}>
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: hex }} />
          {titleCase(region.region)}
        </h3>
        <span className="text-xs text-gray-500">{region.flags} flag{region.flags === 1 ? '' : 's'}</span>
      </div>

      {region.byCategory.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {region.byCategory.map((c) => (
            <div key={c.category} className="flex items-center gap-2">
              <span className="text-xs w-44 truncate" style={{ color: CAT_COLOR[c.category] || '#9ca3af' }}>{c.category}</span>
              <div className="flex-1 h-2.5 rounded" style={{ backgroundColor: '#142236' }}>
                <div className="h-2.5 rounded" style={{ width: `${(c.count / maxCat) * 100}%`, backgroundColor: CAT_COLOR[c.category] || '#9ca3af' }} />
              </div>
              <span className="text-xs text-gray-400 w-6 text-right">{c.count}</span>
            </div>
          ))}
        </div>
      )}

      {region.offenders.length === 0 ? (
        <p className="text-xs text-gray-600 italic">No named offenders.</p>
      ) : (
        <div className="space-y-1">
          {region.offenders.map((o) => (
            <div key={o.tech} className="flex items-center justify-between text-sm px-2 py-1 rounded" style={{ backgroundColor: '#142236' }}>
              <button
                onClick={() => onTechClick(o.tech)}
                className="text-gray-200 font-bold hover:text-gold-400 hover:underline transition text-left"
                title={`View all of ${o.tech}'s QC issues`}
              >
                {o.tech}
              </button>
              <span className="text-xs text-gray-400">
                {o.flags} flag{o.flags === 1 ? '' : 's'}
                {o.top_issue && <span style={{ color: CAT_COLOR[o.top_issue] || '#9ca3af' }}> · {o.top_issue}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QCTicket({ ticket, onPatch, onDelete, onResolveClick }) {
  const [tech, setTech] = useState(ticket.tech || '');
  useEffect(() => { setTech(ticket.tech || ''); }, [ticket.id, ticket.tech]);

  async function saveTech() {
    const v = tech.trim();
    if (v === (ticket.tech || '')) return;
    await onPatch(ticket.id, { tech: v });
  }

  return (
    <div className="border rounded-lg p-4" style={{ backgroundColor: ticket.resolved ? '#0b1622' : '#0d1b2e', borderColor: ticket.resolved ? '#13212f' : '#1e3a5f' }}>
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-300 min-w-0 flex-wrap">
          <span className="font-mono text-gray-400 shrink-0">{ticket.project_id || '—'}</span>
          {ticket.client && <span className="text-gray-500 truncate">· {ticket.client}</span>}
          <span className="flex items-center gap-1 text-gray-500 shrink-0">
            · PM
            <input
              value={tech}
              onChange={(e) => setTech(e.target.value)}
              onBlur={saveTech}
              placeholder="—"
              maxLength={120}
              title="Offender — the project's Site Tracker PM (editable)"
              className="w-32 rounded px-1.5 py-0.5 text-xs text-white placeholder-gray-600 border focus:outline-none"
              style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
            />
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
          <span>{ticket.qcd_by} · {fmtDate(ticket.created_at)}</span>
          <button onClick={() => onDelete(ticket.id)} className="hover:text-red-400" title="Delete">✕</button>
        </div>
      </div>

      {/* The issue */}
      <p className="text-sm text-gray-100 whitespace-pre-wrap mb-3">{ticket.note || <span className="text-gray-600 italic">No note</span>}</p>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-gray-500">Category:</span>
        <select
          value={ticket.category || ''}
          onChange={(e) => onPatch(ticket.id, { category: e.target.value })}
          className="text-xs rounded px-2 py-1 border font-bold focus:outline-none"
          style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: CAT_COLOR[ticket.category] || '#c9a84c' }}
        >
          <option value="">—</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        {ticket.resolved ? (
          <button
            onClick={() => onPatch(ticket.id, { resolved: false })}
            className="ml-auto text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded border text-gray-300 border-gray-600 hover:bg-white/5 transition"
          >
            ↩ Reopen
          </button>
        ) : (
          <button
            onClick={() => onResolveClick(ticket)}
            className="ml-auto text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded transition"
            style={{ backgroundColor: '#22c55e', color: '#06210f' }}
          >
            ✓ Resolve
          </button>
        )}
      </div>

      {/* Resolution (closed tickets) */}
      {ticket.resolved && ticket.resolution_notes && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: '#13212f' }}>
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Resolution</p>
          <p className="text-sm text-gray-300 whitespace-pre-wrap">{ticket.resolution_notes}</p>
        </div>
      )}
    </div>
  );
}

export default function QCPage() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open'); // open | closed
  const [resolving, setResolving] = useState(null); // ticket being resolved
  const [showRegions, setShowRegions] = useState(false); // Mistakes by Region, collapsed by default
  const [viewTech, setViewTech] = useState(null); // person whose full QC history is open
  const [showReport, setShowReport] = useState(false); // monthly per-region PDF report

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([api.get('/qc'), api.get('/qc/stats')]);
      setTickets(t.data);
      setStats(s.data);
    } catch {}
    setLoading(false);
  }

  function refreshStats() {
    api.get('/qc/stats').then((r) => setStats(r.data)).catch(() => {});
  }

  async function patch(id, body) {
    try {
      const { data } = await api.patch(`/qc/${id}`, body);
      setTickets((prev) => prev.map((x) => (x.id === id ? data : x)));
      refreshStats();
      return data;
    } catch {
      alert('Save failed. Please try again.');
    }
  }

  async function resolveTicket(id, resolution_notes) {
    await patch(id, { resolved: true, resolution_notes });
    setResolving(null);
  }

  async function remove(id) {
    if (!window.confirm('Delete this QC ticket?')) return;
    try {
      await api.delete(`/qc/${id}`);
      setTickets((prev) => prev.filter((x) => x.id !== id));
      refreshStats();
    } catch {}
  }

  const shown = tickets.filter((t) => (tab === 'open' ? !t.resolved : t.resolved));
  const openCount = tickets.filter((t) => !t.resolved).length;
  const closedCount = tickets.filter((t) => t.resolved).length;
  const maxCount = stats?.byCategory?.[0]?.count || 1;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-2xl font-black text-white tracking-wide">🔍 Quality Control</h1>
          <button
            onClick={() => setShowReport(true)}
            className="text-xs px-3 py-2 rounded font-bold uppercase tracking-wide transition"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
            title="Monthly QC report — one PDF per region"
          >
            📄 Monthly Report
          </button>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-gray-300 uppercase tracking-widest">Mistakes by Category</h2>
                <span className="text-xs text-gray-500">{stats.total} total</span>
              </div>
              {stats.byCategory.length === 0 ? (
                <p className="text-xs text-gray-600 italic">No QC tickets yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {stats.byCategory.map((c) => (
                    <div key={c.category} className="flex items-center gap-2">
                      <span className="text-xs w-52 truncate" style={{ color: CAT_COLOR[c.category] || '#9ca3af' }}>{c.category}</span>
                      <div className="flex-1 h-3 rounded" style={{ backgroundColor: '#142236' }}>
                        <div className="h-3 rounded" style={{ width: `${(c.count / maxCount) * 100}%`, backgroundColor: CAT_COLOR[c.category] || '#9ca3af' }} />
                      </div>
                      <span className="text-xs text-gray-400 w-16 text-right">{c.count} · {Math.round((c.count / stats.total) * 100)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border rounded-lg p-4" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
              <h2 className="text-sm font-bold text-gray-300 uppercase tracking-widest mb-3">Repeat Offenders (2+ flags)</h2>
              {stats.offenders.length === 0 ? (
                <p className="text-xs text-gray-600 italic">Nobody with 2+ flags yet.</p>
              ) : (
                <div className="space-y-1">
                  {stats.offenders.map((o) => (
                    <div key={o.tech} className="flex items-center justify-between text-sm px-2 py-1 rounded" style={{ backgroundColor: '#142236' }}>
                      <button
                        onClick={() => setViewTech(o.tech)}
                        className="text-gray-200 font-bold hover:text-gold-400 hover:underline transition text-left"
                        title={`View all of ${o.tech}'s QC issues`}
                      >
                        {o.tech}
                      </button>
                      <span className="text-xs text-gray-400">
                        {o.flags} flags
                        {o.top_issue && <span style={{ color: CAT_COLOR[o.top_issue] || '#9ca3af' }}> · {o.top_issue}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mistakes by Region — offenders mapped to their directory region */}
        {stats?.byRegion?.length > 0 && (
          <div>
            <button
              onClick={() => setShowRegions((v) => !v)}
              className="flex items-center gap-2 text-sm font-bold text-gray-300 uppercase tracking-widest hover:text-white transition"
            >
              <span className="text-gray-500 text-xs w-3">{showRegions ? '▼' : '▶'}</span>
              Mistakes by Region
              <span className="text-xs text-gray-500 normal-case tracking-normal font-normal">
                ({stats.byRegion.length} region{stats.byRegion.length === 1 ? '' : 's'})
              </span>
            </button>
            {showRegions && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">
                {stats.byRegion.map((r) => <RegionCard key={r.region} region={r} onTechClick={setViewTech} />)}
              </div>
            )}
          </div>
        )}

        {/* Open / Closed tabs */}
        <div className="flex gap-2">
          {[{ k: 'open', label: `Open (${openCount})` }, { k: 'closed', label: `Closed (${closedCount})` }].map((f) => (
            <button
              key={f.k}
              onClick={() => setTab(f.k)}
              className="text-xs font-bold uppercase tracking-wide px-4 py-1.5 rounded border transition"
              style={tab === f.k
                ? { backgroundColor: '#c9a84c', color: '#0d1b2e', borderColor: '#c9a84c' }
                : { color: '#9ca3af', borderColor: '#1e3a5f' }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Tickets */}
        {loading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="text-gray-500 text-sm italic">
            {tab === 'open' ? 'No open QC tickets. 🎉' : 'No closed tickets yet.'}
          </p>
        ) : (
          <div className="space-y-3">
            {shown.map((t) => (
              <QCTicket key={t.id} ticket={t} onPatch={patch} onDelete={remove} onResolveClick={setResolving} />
            ))}
          </div>
        )}
      </div>

      {resolving && (
        <ResolveModal ticket={resolving} onClose={() => setResolving(null)} onResolve={resolveTicket} />
      )}

      {viewTech && (
        <TechHistoryModal tech={viewTech} tickets={tickets} onClose={() => setViewTech(null)} />
      )}

      {showReport && <QCReportModal onClose={() => setShowReport(false)} />}
    </Layout>
  );
}
